import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  OfficeBuildingIcon,
  PlusIcon,
  UserIcon,
} from "@heroicons/react/outline";
import AccountingWrapper from "../../../../Components/v2/Accounting/AccountingWrapper";
import TableActions from "../../../../Components/v2/Table/TableActions";
import Table from "../../../../Components/v2/Table";
import AccountingPageHeader from "../../../../Components/v2/Accounting/AccountingPageHeader";
import { useTranslation } from "react-i18next";
import {
  addLocalNotification,
  clearBusinessPartner,
  clearBusinessPartnerError,
  requestDeleteBusinessPartner,
  requestListBusinessPartner,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  useNotificationDispatch,
} from "../../../../Context";
import Pagination from "../../../../Components/v2/Pagination";
import ContactModal from "../../../../Components/v2/Accounting/ContactModal";
import ContactDetailModal from "../../../../Components/v2/Accounting/ContactDetailModal";
import { ModalAlert } from "../../../../Components/ModalAlert";
import { cancelAPIRequest } from "../../../../Utils";
import ErrorDialog from "../../../../Components/v2/ErrorDialog";

const Contacts = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(
    location.state?.partner ?? null,
  );
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const dispatch = useBusinessTransactionDispatch();
  const notificationDispatch = useNotificationDispatch();

  const { user } = useAuthState();
  const {
    request,
    businessPartners,
    businessPartnerLoading,
    businessPartnerError,
    businessPartnerNextTokens,
    deletePartnerLoading,
    deletePartnerError,
    deletedID,
  } = useBusinessTransactionState();

  useEffect(() => {
    return () => {
      cancelAPIRequest(request);
      clearBusinessPartner(dispatch);
      clearBusinessPartnerError(dispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open detail modal if navigated from another page with a partner in state
  useEffect(() => {
    if (location.state?.partner) {
      setDetailModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user?.mainBusiness?.id) {
      setPage(1);
      requestListBusinessPartner(dispatch, {
        page: 1,
        limit: perPage,
        businessID: user.mainBusiness.id,
      });
    }
  }, [dispatch, perPage, user?.mainBusiness?.id]);

  //  Handle delete success
  useEffect(() => {
    if (deletedID) {
      // Close modal
      setShowConfirmModal(false);
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `delete-contact-${deletedID}`,
        type: "success",
        title: "common.success",
        body: "contacts.notifications.deleteSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, deletedID]);

  useEffect(() => {
    if (deletePartnerError) {
      setShowConfirmModal(false);
    }
  }, [deletePartnerError]);

  useEffect(() => {
    if (deletePartnerError && !showConfirmModal) {
      setShowErrorModal(true);
    }
  }, [deletePartnerError, showConfirmModal]);

  const onPageChange = (selectedPage) => {
    setPage(selectedPage);
    if (businessPartnerNextTokens[selectedPage] || selectedPage === 1) {
      requestListBusinessPartner(dispatch, {
        page: selectedPage,
        limit: perPage,
        businessID: user?.mainBusiness?.id,
        nextTokens: businessPartnerNextTokens[selectedPage],
      });
    }
  };

  const onPerPageChange = (itemsPerPage) => {
    setPerPage(itemsPerPage);
  };

  const handleAdd = () => {
    setSelectedPartner(null);
    setContactModalOpen(true);
  };

  const handleViewDetail = (partner) => {
    setSelectedPartner(partner);
    setDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedPartner(null);
  };

  const handleEditFromDetail = (partner) => {
    setDetailModalOpen(false);
    setSelectedPartner(partner);
    setContactModalOpen(true);
  };

  const handleEdit = (partner) => {
    setSelectedPartner(partner);
    setContactModalOpen(true);
  };

  const handleDelete = (partner) => {
    setSelectedPartner(partner);
    setShowConfirmModal(true);
  };

  const onDelete = () => {
    requestDeleteBusinessPartner(dispatch, {
      id: selectedPartner?.id,
    });
  };

  const getDeleteDescription = () => {
    if (!selectedPartner) return null;
    const type =
      selectedPartner.type === "PERSONAL"
        ? t("businesses.types.personal")
        : t("businesses.types.business");
    const info = selectedPartner.email
      ? `${t("contacts.fields.email")} ${selectedPartner.email}`
      : selectedPartner.phone
        ? `${t("contacts.fields.phone")} ${selectedPartner.phone}`
        : null;
    return t(
      info
        ? "contacts.modal.description.deleteItem"
        : "contacts.modal.description.deleteItemNoInfo",
      {
        name: selectedPartner.name,
        type,
        info,
        interpolation: { escapeValue: false },
      },
    );
  };

  const handleClose = () => {
    cancelAPIRequest(request);
    setContactModalOpen(false);
    setSelectedPartner(null);
  };

  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        {/* Page header */}
        <AccountingPageHeader
          title="contacts.label"
          primaryAction={{
            label: t("contacts.header.title.add"),
            onClick: handleAdd,
            icon: <PlusIcon className="h-5 w-5" />,
          }}
        />

        {/* Contacts Table */}
        <div className="px-4 py-5 sm:px-6 lg:px-8">
          <Table
            isLoading={businessPartnerLoading}
            error={businessPartnerError}
            onRetry={() => onPageChange(page)}
            columns={[
              {
                header: "contacts.fields.name",
                sortable: true,
                sortKey: "name",
                accessor: (contact) => (
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                      {contact.type === "PERSONAL" ? (
                        <UserIcon className="h-6 w-6 text-gray-400" />
                      ) : (
                        <OfficeBuildingIcon className="h-6 w-6 text-gray-400" />
                      )}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {contact.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {contact.type === "PERSONAL"
                          ? t("businesses.types.personal")
                          : t("businesses.types.business")}
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                header: "contacts.fields.info",
                accessor: (contact) => {
                  if (!contact.email && !contact.phone) return "-";

                  return (
                    <div>
                      <div className="text-sm text-gray-900">
                        {contact.email || "-"}
                      </div>
                      <div className="text-sm text-gray-500">
                        {contact.phone || "-"}
                      </div>
                    </div>
                  );
                },
              },
              {
                header: t("contacts.fields.address"),
                accessor: (contact) => contact.address || "-",
                className: "text-gray-500",
              },
              {
                header: "common.actions.label",
                accessor: (contact, index) => (
                  <TableActions
                    position={
                      index === businessPartners.length - 1 ? "top" : "bottom"
                    }
                    actions={[
                      {
                        label: t("common.view"),
                        onClick: () => handleViewDetail(contact),
                      },
                      {
                        label: t("common.edit"),
                        onClick: () => handleEdit(contact),
                      },
                      {
                        label: t("common.delete"),
                        onClick: () => handleDelete(contact),
                        variant: "danger",
                        disabled: Boolean(contact.inUse),
                        tooltip: contact.inUse
                          ? t("common.inUseInTransactions")
                          : undefined,
                      },
                    ]}
                  />
                ),
                align: "right",
              },
            ]}
            data={businessPartners}
          />
        </div>

        {/* Pagination */}
        <Pagination
          nextTokens={businessPartnerNextTokens}
          currentPage={page}
          isLoading={businessPartnerLoading}
          onPageChange={onPageChange}
          perPage={perPage}
          onPerPageChange={onPerPageChange}
        />

        {/* Contact Modal */}
        {contactModalOpen && (
          <ContactModal
            open={contactModalOpen}
            onClose={handleClose}
            partner={selectedPartner}
          />
        )}

        {/* Detail Modal */}
        {detailModalOpen && (
          <ContactDetailModal
            open={detailModalOpen}
            onClose={handleCloseDetailModal}
            partner={selectedPartner}
            onEdit={handleEditFromDetail}
          />
        )}

        {/* Delete Modal */}
        <ModalAlert
          open={showConfirmModal}
          setOpen={(open) => {
            setShowConfirmModal(open);
          }}
          title="contacts.modal.title.delete"
          description="contacts.modal.description.delete"
          descriptionNode={getDeleteDescription()}
          confirmButtonText="common.delete"
          cancelButtonText="Cancel"
          loadingButton={deletePartnerLoading}
          disabledButton={deletePartnerLoading}
          onConfirm={onDelete}
          onCancel={() => {
            cancelAPIRequest(request);
            setShowConfirmModal(false);
          }}
        />

        {/* Error Modal */}
        {deletePartnerError && (
          <ErrorDialog
            open={showErrorModal}
            error={deletePartnerError}
            onClose={() => {
              setShowErrorModal(false);
              clearBusinessPartnerError(dispatch);
            }}
          />
        )}
      </main>
    </AccountingWrapper>
  );
};

export default Contacts;
